import {isFeatureEnabled} from '@github-ui/feature-flags'
import {useTrackingRef} from '@github-ui/use-tracking-ref'
import {RowsIcon} from '@primer/octicons-react'
import {Button, useConfirm} from '@primer/react'
import {type HTMLAttributes, memo, useCallback, useImperativeHandle, useRef} from 'react'

import {shortcutFromEvent, SHORTCUTS} from '../../helpers/keyboard-shortcuts'
import {isViewConfigDirty} from '../../hooks/use-view-state-reducer/get-view-dirty-states'
import {useViews} from '../../hooks/use-views'
import {useViewOptionsMenuRef} from '../../state-providers/view-options/use-view-options-menu-ref'
import {Resources} from '../../strings'
import {DirtyViewIndicator} from '../view-options/menu-anchor'
import {FilterBar, type FilterBarProps} from './filter-bar'
import {FilterContainer} from './filter-container'
import {FilterCounterLabel} from './filter-counter-label'
import {FilterInputActions} from './filter-input-actions'
import styles from './tokenized-filter-input.module.css'

export type TokenizedFilterInputProps = {
  /**
   * The value of the input. It will be parsed and displayed as colored
   * tokens, as well as populate the input
   */
  value: string
  /**
   * A handler for the input value change
   */
  onChangeValue: (value: string) => void
  /**
   * A handler for the input keydown event
   */
  onInputKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void
  /**
   * A ref for the input, optional
   */
  inputRef?: React.RefObject<HTMLInputElement | null>
  /**
   * When given, a count of results is displayed to the right of the input.
   */
  filterCount?: number | null | undefined
  /**
   * An optional handler for the reset changes button, when passed the button will be shown
   */
  onResetChanges?: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void
  /**
   * An optional handler for the save changes button, when passed the button will be shown
   */
  onSaveChanges?: (e: React.MouseEvent<HTMLButtonElement> | React.KeyboardEvent<HTMLButtonElement>) => void
  /**
   * Optional; customize text to display on the save changes button
   */
  saveButtonText?: string
  /**
   * Whether to hide the save button; defaults to false
   */
  hideSaveButton?: boolean
  /**
   * Whether to hide the reset changes button; defaults to false
   */
  hideResetChangesButton?: boolean
  /**
   * Whether to hide the counter label; defaults to false
   */
  hideCounterLabel?: boolean
} & Pick<HTMLAttributes<HTMLInputElement>, 'aria-describedby'> &
  Pick<FilterBarProps, 'limitedProviderKeys' | 'onValidation' | 'context'>

export const TokenizedFilterInput = memo<React.PropsWithChildren<TokenizedFilterInputProps>>(
  function TokenizedFilterInput({
    value,
    onChangeValue,
    onInputKeyDown,
    inputRef: givenInputRef,
    filterCount,
    onResetChanges,
    onSaveChanges,
    saveButtonText,
    hideSaveButton = false,
    hideCounterLabel = false,
    hideResetChangesButton = false,
    children,
    onValidation,
    limitedProviderKeys,
    context,
  }) {
    const inputRef = useRef<HTMLInputElement>(null)
    const confirm = useConfirm()
    useImperativeHandle(givenInputRef, () => inputRef.current)

    const onInputKeyDownRef = useTrackingRef(onInputKeyDown)
    const handleInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
      e => {
        switch (shortcutFromEvent(e)) {
          case SHORTCUTS.ESCAPE: {
            onInputKeyDownRef.current?.(e)
            return
          }
          case SHORTCUTS.ARROW_DOWN: {
            // TODO: Should only trigger callback when Filter suggestions are hidden
            // https://github.com/github/projects-platform/issues/3408
            // onInputKeyDownRef.current?.(e)
            return
          }
          case SHORTCUTS.TAB: {
            // When a value is present, defer to default Filter component behavior
            if (value.length > 0) return
            onInputKeyDownRef.current?.(e)
            return
          }
        }
      },
      [onInputKeyDownRef, value.length],
    )

    const handleResetChanges: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation()
        onResetChanges?.(event)
        inputRef.current?.focus()
      },
      [onResetChanges],
    )

    const displayButtonEnabled = isFeatureEnabled('memex_display_button_config_menu')
    const {currentView, isViewStateDirty} = useViews()

    const configDirty = currentView ? isViewConfigDirty(currentView) : false
    const isConfigDirty = displayButtonEnabled ? configDirty : isViewStateDirty

    const handleSaveChanges: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      async event => {
        event.stopPropagation()
        if (!onSaveChanges) {
          return
        }

        if (displayButtonEnabled) {
          const shouldSave = await confirm({
            title: Resources.confirmSetDefaultChangesTitle(currentView?.name),
            content: Resources.confirmSetDefaultChangesDescription,
            confirmButtonContent: Resources.saveChanges,
            confirmButtonType: 'primary',
            width: 'large',
          })

          if (shouldSave) {
            onSaveChanges(event)
          }
        } else {
          onSaveChanges(event)
        }

        inputRef.current?.focus()
      },
      [confirm, currentView?.name, displayButtonEnabled, onSaveChanges],
    )

    const hasResetAction = Boolean(onResetChanges) && !hideResetChangesButton
    const hasSaveAction = Boolean(onSaveChanges) && !hideSaveButton
    const shouldRenderFilterInputActions = hasResetAction || hasSaveAction
    const {anchorRef, toggleMenu, isMenuOpen} = useViewOptionsMenuRef()

    const handleDisplayButtonClick: React.MouseEventHandler<HTMLButtonElement> = useCallback(
      event => {
        event.stopPropagation()
        toggleMenu()
      },
      [toggleMenu],
    )

    return (
      <FilterContainer>
        <div className={styles.Box}>
          <FilterBar
            filterValue={value}
            onChange={onChangeValue}
            onValidation={onValidation}
            onKeyDown={handleInputKeyDown}
            inputRef={inputRef}
            context={context}
            limitedProviderKeys={limitedProviderKeys}
            trailingVisual={<FilterCounterLabel hideCounterLabel={hideCounterLabel} filterCount={filterCount} />}
          />
        </div>
        {(!displayButtonEnabled || shouldRenderFilterInputActions) && (
          <>
            <FilterInputActions
              hideSaveButton={hideSaveButton}
              hideResetChangesButton={hideResetChangesButton}
              onResetChanges={onResetChanges ? handleResetChanges : undefined}
              onSaveChanges={onSaveChanges && !hideSaveButton ? handleSaveChanges : undefined}
              saveButtonText={saveButtonText}
            >
              {children}
            </FilterInputActions>
            {displayButtonEnabled && <div className={styles.Divider} aria-hidden="true" role="presentation" />}
          </>
        )}
        {displayButtonEnabled ? (
          <DirtyViewIndicator isViewStateDirty={isConfigDirty}>
            <Button
              ref={anchorRef}
              leadingVisual={RowsIcon}
              onClick={handleDisplayButtonClick}
              aria-haspopup="menu"
              aria-expanded={isMenuOpen}
            >
              Display
            </Button>
          </DirtyViewIndicator>
        ) : null}
      </FilterContainer>
    )
  },
)

try{ TokenizedFilterInput.displayName ||= 'TokenizedFilterInput' } catch {}
try{ TokenizedFilterInput.displayName ||= 'TokenizedFilterInput' } catch {}